import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  Edit, Trash2, MapPin, Sparkles, TrendingUp, Navigation2,
  Home, Camera, MessageSquare, Star, Info,
} from "lucide-react";
import { toast } from "sonner";
import { calculateQuickScore, verdictFromScore } from "../utils/quickScore";
import { CATEGORY_COLORS } from "../constants/propertyOptions";
import { formatMoney } from "../utils/currency";
import { PROPERTY } from "../constants/testIds";
import NavigationDialog from "./NavigationDialog";
import PhotoUploader from "./PhotoUploader";
import MeetingNotesTimeline from "./MeetingNotesTimeline";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PropertyDrawer = ({ property: propProperty, open, onClose, onEdit, onDelete }) => {
  const [aiResponse, setAiResponse] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [property, setProperty] = useState(propProperty);

  useEffect(() => {
    setProperty(propProperty);
    setAiResponse("");
    setAiLoading(false);
    // Depending on propProperty?.id is enough — the identity of propProperty
    // itself changes on every parent render, but we only want to re-init
    // when the selected property changes or the drawer toggles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propProperty?.id, open]);

  const refreshProperty = async () => {
    if (!property?.id) return;
    try {
      const { data } = await axios.get(`${API_URL}/api/properties`, { withCredentials: true });
      const updated = data.find((p) => p.id === property.id);
      if (updated) setProperty(updated);
    } catch (e) {
      console.debug("refreshProperty failed:", e?.message || e);
    }
  };

  if (!property) return null;

  const rawCurrent = property.current_price ?? property.price;
  const currentPrice = (typeof rawCurrent === "number" && rawCurrent > 0) ? rawCurrent : null;
  const rawArea = property.net_area || property.area;
  const netArea = (typeof rawArea === "number" && rawArea > 0) ? rawArea : null;
  const currency = property.currency || "TRY";
  const currencyOther = property.currency_other || "";
  const ppm2 = netArea && currentPrice ? currentPrice / netArea : null;
  const catColor = CATEGORY_COLORS[property.category] || "#9CA3AF";
  const score = calculateQuickScore(property);
  const verdict = verdictFromScore(score.score, score.completeness);
  const scoreReady = score.completeness >= 25;

  const handleAiAnalyze = async () => {
    setAiLoading(true); setAiResponse("");
    try {
      const result = await fetch(`${API_URL}/api/ai/analyze-single/${property.id}`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      });
      const reader = result.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split("\n")) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") break;
            setAiResponse((p) => p + data);
          }
        }
      }
    } catch (e) { toast.error("Analiz sırasında hata"); }
    finally { setAiLoading(false); }
  };

  const photos = property.photos || [];
  const mainPhoto = photos.find(p => p.is_main) || photos[0];
  const mainPhotoUrl = mainPhoto ? `${API_URL}/api/photos/${mainPhoto.id}` : property.image_url;

  return (
    <>
      <Sheet open={open} onOpenChange={onClose}>
        <SheetContent
          data-testid={PROPERTY.detailsDrawer}
          className="w-full sm:max-w-2xl bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A] overflow-y-auto p-0"
        >
          {/* Header */}
          <SheetHeader className="p-6 pb-4 border-b border-[#E4E4E7] dark:border-[#27272A]">
            <div className="flex items-start gap-3">
              <div
                className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: catColor }}
              >
                <MapPin className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <SheetTitle className="text-xl sm:text-2xl font-bold text-[#0A0A0A] dark:text-[#FAFAFA] leading-tight">
                  {property.title}
                </SheetTitle>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  {property.code && (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-[#52525B] dark:text-[#A1A1AA] bg-[#F9FAFB] dark:bg-[#09090B] px-2 py-0.5 rounded">
                      {property.code}
                    </span>
                  )}
                  {property.status && (
                    <span className="text-[10px] uppercase tracking-widest font-bold text-white px-2 py-0.5 rounded" style={{ backgroundColor: catColor }}>
                      {property.status}
                    </span>
                  )}
                  <span className="text-xs text-[#52525B] dark:text-[#A1A1AA]">{property.category}</span>
                </div>
              </div>
            </div>

            {/* Quick actions row */}
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {property.lat && property.lng && (
                <Button
                  size="sm"
                  data-testid="drawer-navigation-button"
                  onClick={() => setNavOpen(true)}
                  className="bg-[#3B82F6] hover:bg-[#2563EB] text-white h-9"
                >
                  <Navigation2 className="w-4 h-4 mr-1" />Yol Tarifi
                </Button>
              )}
              <Button size="sm" onClick={() => onEdit(property)} variant="outline" className="h-9 border-[#E4E4E7] dark:border-[#27272A]" data-testid={PROPERTY.editButton}>
                <Edit className="w-4 h-4 mr-1" />Düzenle
              </Button>
              <Button size="sm" onClick={handleAiAnalyze} disabled={aiLoading} className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-white h-9">
                <Sparkles className={`w-4 h-4 mr-1 ${aiLoading ? "animate-pulse" : ""}`} />AI Analiz
              </Button>
              <Button size="sm" onClick={() => {
                if (window.confirm("Bu gayrimenkul silinsin mi?")) onDelete(property.id);
              }} variant="outline" className="h-9 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400" data-testid={PROPERTY.deleteButton}>
                <Trash2 className="w-4 h-4 mr-1" />Sil
              </Button>
            </div>
          </SheetHeader>

          {/* Score card */}
          <div className="px-6 pt-4">
            {scoreReady ? (
              <div className="p-4 rounded-lg border" style={{ borderColor: verdict.color + "40", backgroundColor: verdict.color + "0D" }}>
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-full flex items-center justify-center text-2xl font-black text-white" style={{ backgroundColor: verdict.color }}>
                    {score.score}
                  </div>
                  <div className="flex-1">
                    <div className="font-bold text-[#0A0A0A] dark:text-[#FAFAFA]">{verdict.label}</div>
                    <div className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
                      AI Puanı {score.score}/100 • Veri Yeterliliği %{score.completeness}
                      {score.provisional && " • geçici"}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-lg border-2 border-dashed border-[#E4E4E7] dark:border-[#27272A] text-center">
                <div className="font-bold text-[#0A0A0A] dark:text-[#FAFAFA]">Henüz puanlanmadı</div>
                <div className="text-xs text-[#52525B] dark:text-[#A1A1AA] mt-1">
                  Veri Yeterliliği %{score.completeness} — puan hesaplaması için minimum %25 gerekli
                </div>
              </div>
            )}
          </div>

          <Tabs defaultValue="ozet" className="px-6 pt-4 pb-24">
            <TabsList className="grid grid-cols-5 h-9">
              <TabsTrigger value="ozet" className="text-xs"><Info className="w-3 h-3 mr-1 hidden sm:inline" />Özet</TabsTrigger>
              <TabsTrigger value="foto" className="text-xs"><Camera className="w-3 h-3 mr-1 hidden sm:inline" />Foto</TabsTrigger>
              <TabsTrigger value="notlar" className="text-xs"><MessageSquare className="w-3 h-3 mr-1 hidden sm:inline" />Notlar</TabsTrigger>
              <TabsTrigger value="fiyat" className="text-xs"><TrendingUp className="w-3 h-3 mr-1 hidden sm:inline" />Fiyat</TabsTrigger>
              <TabsTrigger value="ai" className="text-xs"><Sparkles className="w-3 h-3 mr-1 hidden sm:inline" />AI</TabsTrigger>
            </TabsList>

            <TabsContent value="ozet" className="mt-4 space-y-4">
              {mainPhotoUrl && (
                <div className="rounded-lg overflow-hidden border border-[#E4E4E7] dark:border-[#27272A]">
                  <img src={mainPhotoUrl} alt={property.title} className="w-full h-56 object-cover" />
                </div>
              )}
              <InfoBlock label="Fiyat">
                <div className="text-2xl font-bold text-[#F59E0B]">
                  {currentPrice != null ? formatMoney(currentPrice, currency, currencyOther) : "Fiyat girilmedi"}
                </div>
                {ppm2 && <div className="text-xs text-[#52525B] dark:text-[#A1A1AA]">{formatMoney(Math.round(ppm2), currency, currencyOther)}/m²</div>}
              </InfoBlock>
              <div className="grid grid-cols-2 gap-3">
                {property.property_type && <InfoBlock label="Tip">{property.property_type}</InfoBlock>}
                {property.room_count && <InfoBlock label="Oda">{property.room_count}</InfoBlock>}
                {netArea && <InfoBlock label="Net m²">{netArea} m²</InfoBlock>}
                {property.gross_area && <InfoBlock label="Brüt m²">{property.gross_area} m²</InfoBlock>}
                {property.heating && <InfoBlock label="Isıtma">{property.heating}</InfoBlock>}
                {property.parking && <InfoBlock label="Otopark">{property.parking}</InfoBlock>}
                {property.deed_type && <InfoBlock label="Tapu">{property.deed_type}</InfoBlock>}
                {property.technical_condition && <InfoBlock label="Teknik">{property.technical_condition}</InfoBlock>}
              </div>
              <InfoBlock label="Konum">
                <div>{property.location || [property.neighborhood, property.district, property.city].filter(Boolean).join(", ")}</div>
                {property.address && <div className="text-xs mt-1 text-[#52525B] dark:text-[#A1A1AA]">{property.address}</div>}
                {property.lat && property.lng && (
                  <div className="text-[10px] text-[#52525B] dark:text-[#A1A1AA] mt-1">
                    {property.lat.toFixed(5)}, {property.lng.toFixed(5)}
                    {property.location_accuracy && ` (±${property.location_accuracy}m)`}
                  </div>
                )}
              </InfoBlock>
              {property.features?.length > 0 && (
                <InfoBlock label="Özellikler">
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {property.features.map((f) => (
                      <span key={f} className="px-2 py-0.5 rounded-full bg-[#F9FAFB] dark:bg-[#09090B] text-[10px] text-[#0A0A0A] dark:text-[#FAFAFA] border border-[#E4E4E7] dark:border-[#27272A]">
                        {f.replace(/_/g, " ")}
                      </span>
                    ))}
                  </div>
                </InfoBlock>
              )}
              {property.description && (
                <InfoBlock label="Açıklama">
                  <p className="whitespace-pre-wrap text-sm">{property.description}</p>
                </InfoBlock>
              )}
              {(property.personal_like || property.location_score || property.rentability_score || property.personal_impression) && (
                <InfoBlock label="Kişisel Puanım">
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    {property.personal_like != null && <PersonalScore label="Beğeni" value={property.personal_like} />}
                    {property.location_score != null && <PersonalScore label="Konum" value={property.location_score} />}
                    {property.rentability_score != null && <PersonalScore label="Kira" value={property.rentability_score} />}
                  </div>
                  {property.personal_impression && (
                    <p className="mt-2 text-sm italic text-[#52525B] dark:text-[#A1A1AA]">"{property.personal_impression}"</p>
                  )}
                </InfoBlock>
              )}
            </TabsContent>

            <TabsContent value="foto" className="mt-4">
              <PhotoUploader
                propertyId={property.id}
                photos={photos}
                onChange={refreshProperty}
              />
            </TabsContent>

            <TabsContent value="notlar" className="mt-4">
              <MeetingNotesTimeline
                propertyId={property.id}
                notes={property.meeting_notes || []}
                onChange={refreshProperty}
              />
            </TabsContent>

            <TabsContent value="fiyat" className="mt-4 space-y-2">
              {[
                { l: "İlk İlan Fiyatı", v: property.initial_price },
                { l: "Güncel İlan Fiyatı", v: property.current_price || property.price },
                { l: "Satıcının Son Fiyatı", v: property.seller_last_price },
                { l: "Teklifim", v: property.my_offer },
                { l: "Karşı Teklif", v: property.counter_offer },
                { l: "Hedef Satın Alma", v: property.target_price },
                { l: "Maks. Satın Alma", v: property.max_price },
                { l: "Anlaşılan Fiyat", v: property.agreed_price },
                { l: "Tahmini Aylık Kira", v: property.estimated_rent },
                { l: "Aidat", v: property.aidat },
                { l: "Tadilat Masrafı", v: property.renovation_cost },
                { l: "Eşya Masrafı", v: property.furniture_cost },
                { l: "Diğer Ek Masraf", v: property.extra_costs },
              ].filter(x => typeof x.v === "number" && x.v > 0).map((x) => (
                <div key={x.l} className="flex justify-between py-2 border-b border-[#E4E4E7] dark:border-[#27272A] text-sm">
                  <span className="text-[#52525B] dark:text-[#A1A1AA]">{x.l}</span>
                  <span className="font-semibold text-[#0A0A0A] dark:text-[#FAFAFA]">{formatMoney(x.v, currency, currencyOther)}</span>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="ai" className="mt-4">
              {!aiResponse && !aiLoading && (
                <div className="text-center py-6 text-xs text-[#52525B] dark:text-[#A1A1AA]">
                  <Sparkles className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  Üstteki "AI Analiz" butonuna basarak fiyat/performans değerlendirmesi başlatın.
                </div>
              )}
              {aiLoading && !aiResponse && (
                <div className="flex items-center justify-center py-6 gap-2">
                  <div className="w-2 h-2 bg-[#F59E0B] rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-[#F59E0B] rounded-full animate-bounce" style={{animationDelay:"0.15s"}} />
                  <div className="w-2 h-2 bg-[#F59E0B] rounded-full animate-bounce" style={{animationDelay:"0.3s"}} />
                </div>
              )}
              {aiResponse && (
                <div className="text-sm whitespace-pre-wrap text-[#0A0A0A] dark:text-[#FAFAFA] leading-relaxed p-4 bg-[#F9FAFB] dark:bg-[#09090B] rounded border border-[#E4E4E7] dark:border-[#27272A]">
                  {aiResponse}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {property.lat && property.lng && (
        <NavigationDialog
          open={navOpen}
          onClose={() => setNavOpen(false)}
          lat={property.lat}
          lng={property.lng}
          title={property.title}
        />
      )}
    </>
  );
};

const InfoBlock = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest font-bold text-[#52525B] dark:text-[#A1A1AA] mb-1">{label}</div>
    <div className="text-sm text-[#0A0A0A] dark:text-[#FAFAFA]">{children}</div>
  </div>
);

const PersonalScore = ({ label, value }) => (
  <div className="flex flex-col items-center p-2 bg-[#F9FAFB] dark:bg-[#09090B] rounded border border-[#E4E4E7] dark:border-[#27272A]">
    <div className="text-[10px] uppercase tracking-widest text-[#52525B] dark:text-[#A1A1AA]">{label}</div>
    <div className="text-lg font-black text-[#F59E0B]">{value}/10</div>
  </div>
);

export default PropertyDrawer;
